import { MessageSquareOff } from "lucide-react";
import { Dispatch, SetStateAction } from "react";

import {
  RequestDisplayData,
  getChatRequestDisplayState,
  getRequestOutputMedia,
} from "./RequestDisplayUtils";

import { Chat } from "@/chat";
import { OutputMediaBlock } from "@/components/ui/MediaBlocks";
import { JSONSchema } from "@/types";
import { truncateBase64InObject } from "@/utils/strings";

interface ChatRequestProps {
  requestInfo: RequestDisplayData;
  isOpenStates: Record<string, boolean | undefined>;
  setIsOpenStates: Dispatch<SetStateAction<Record<string, boolean | undefined>>>;
  requestId: string;
  jsonSchema?: {
    json_schema: JSONSchema;
    type: "json_schema";
  };
}

export const NoResponse = () => (
  <div className="flex flex-col items-center justify-center px-6 py-12">
    <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-gray-50/80">
      <MessageSquareOff className="h-6 w-6 text-gray-300" strokeWidth={1.5} />
    </div>
    <h3 className="mb-1.5 text-[13px] font-medium text-gray-700">No response</h3>
    <p className="text-center text-[12px] leading-relaxed text-gray-400">
      The LLM did not return a response for this request.
    </p>
  </div>
);

export const ChatRequest = ({
  requestInfo,
  isOpenStates,
  setIsOpenStates,
  requestId,
  jsonSchema,
}: ChatRequestProps) => {
  const chatTemplate = requestInfo.prompt_version?.prompt_template;
  if (chatTemplate?.type !== "chat") {
    return <div className="rounded border border-red-300 p-2">Invalid prompt format</div>;
  }

  const hasTools = Boolean(chatTemplate.tools?.length);
  const hasFunctions = Boolean(chatTemplate.functions?.length);
  const isGoogle = requestInfo.prompt_version?.metadata?.model?.provider === "google";
  const { requestMessages } = getChatRequestDisplayState(chatTemplate);

  return (
    <div className="space-y-1 overflow-y-auto pr-1">
      {jsonSchema && (
        <Chat isGoogle={isGoogle} chatTemplate={{ ...chatTemplate, json_schema: jsonSchema }} />
      )}
      {hasTools && <Chat chatTemplate={chatTemplate} />}
      {!hasTools && hasFunctions && <Chat chatTemplate={chatTemplate} />}
      {requestMessages.map((message, index) => {
        const key = `${requestId}.${requestInfo.request_text?.length}.${index}`;
        const isOpen = isOpenStates[key];

        return (
          <div key={key} className="text-sm">
            <Chat
              inputVariables={requestInfo.prompt_input_variables}
              startOpen={index === requestInfo.request_text?.length - 1}
              isOpen={isOpen}
              onToggle={() => {
                setIsOpenStates(current => ({
                  ...current,
                  [key]: !current[key],
                }));
              }}
              message={message}
              chatTemplate={chatTemplate}
            />
          </div>
        );
      })}
    </div>
  );
};

export const ChatResponse = ({
  requestResponse,
  requestInfo,
}: {
  requestResponse: unknown;
  requestInfo: RequestDisplayData;
}) => {
  const chatTemplate = requestInfo.prompt_version?.prompt_template;
  if (chatTemplate?.type !== "chat") {
    return <NoResponse />;
  }

  const { hasResponseContent, responseMessage } = getChatRequestDisplayState(chatTemplate);
  if (requestInfo.status === "ERROR" && !responseMessage) {
    return <NoResponse />;
  }

  if (responseMessage && hasResponseContent) {
    return (
      <div className="space-y-1 overflow-y-auto pr-1">
        <div className="text-sm">
          <Chat startOpen message={responseMessage} chatTemplate={chatTemplate} />
        </div>
      </div>
    );
  }

  if (!responseMessage || !hasResponseContent) {
    return <NoResponse />;
  }

  if (requestInfo.request_response) {
    return (
      <pre className="rounded border p-2">
        {JSON.stringify(truncateBase64InObject(requestResponse), undefined, 2)}
      </pre>
    );
  }

  return <NoResponse />;
};

export const ImageResponse = ({ requestInfo }: { requestInfo: RequestDisplayData }) => {
  const outputMedia = getRequestOutputMedia(requestInfo);

  if (!outputMedia?.length) {
    return <div className="rounded border border-red-300 p-2">No media found</div>;
  }

  const [firstMedia, ...extraMedia] = outputMedia;

  return (
    <div className="flex flex-col gap-4 overflow-y-auto rounded-sm border border-gray-300 bg-gray-100 p-4">
      <OutputMediaBlock
        key={firstMedia.id ?? firstMedia.url}
        content={firstMedia}
        extraImages={extraMedia}
        readonly
      />
    </div>
  );
};
